import axios, { type AxiosInstance } from "axios";
import i18n from "../locales/i18n";
import { config } from "../config";
import { vgsPostUrl } from "../utils/tokenization-config";
import { getDeviceSession } from "../device/seon-session";
import type {
  IWidgetSession,
  ITermsBlock,
} from "../shared/session/interfaces/session";
import { LINK_ERROR_CODE, RequestStatus } from "../utils/commons";
import { resolveUrlLinkId } from "../utils/resolveUrlLinkId";
import { resolveXLinkIdHeader } from "./resolveXLinkIdHeader";
import { useSessionStore } from "../store/useSessionStore";
import { useOfflineStore } from "../store/useOfflineStore";
import { isAxiosNetworkFailure } from "../utils/axiosNetworkError";

/**
 * Error class for link claim errors
 */
export interface WidgetApiError {
  code: string;
  message: string;
  details?: unknown;
}

export const getWidgetApiError = (error: unknown): WidgetApiError | null => {
  if (!axios.isAxiosError(error)) return null;
  const body = error.response?.data?.error;
  if (!body || typeof body.code !== "string") return null;
  return {
    code: body.code,
    message: typeof body.message === "string" ? body.message : "",
    details: body.details,
  };
};

export class LinkClaimError extends Error {
  constructor(
    message: string,
    public code: LINK_ERROR_CODE,
    public statusCode?: number,
    public details?: unknown,
  ) {
    super(message);
    this.name = "LinkClaimError";
  }
}

/**
 * Codes that make the link permanently unusable: the widget cannot recover from
 * them and must show the matching explanation instead of a generic error.
 */
const DEAD_LINK_CODES: string[] = [
  LINK_ERROR_CODE.LINK_NOT_FOUND,
  LINK_ERROR_CODE.LINK_EXPIRED,
  LINK_ERROR_CODE.LINK_REVOKED,
];

/**
 * Resolves the claim/API failure into a link error code. The backend `code`
 * wins over the HTTP status: a revoked link answers 409 `LINK_REVOKED` on
 * claim and 403 `LINK_REVOKED` on the partner CIP ticket, so status alone
 * cannot tell "revoked" from "already used".
 */
const resolveLinkErrorCode = (
  status: number | undefined,
  apiCode: string | undefined
): LINK_ERROR_CODE => {
  if (apiCode && apiCode in LINK_ERROR_CODE) {
    return apiCode as LINK_ERROR_CODE;
  }

  switch (status) {
    case 400:
      return LINK_ERROR_CODE.INVALID_TOKEN;
    case 404:
      return LINK_ERROR_CODE.LINK_NOT_FOUND;
    case 409:
      return LINK_ERROR_CODE.LINK_ALREADY_USED;
    case 410:
      return LINK_ERROR_CODE.LINK_EXPIRED;
    case 429:
      return LINK_ERROR_CODE.RATE_LIMITED;
    default:
      // No status means the request never reached the backend (network/CORS).
      return status === undefined || status >= 500
        ? LINK_ERROR_CODE.SERVICE_UNAVAILABLE
        : LINK_ERROR_CODE.INTERNAL_ERROR;
  }
};

/**
 * Custom event for dead links (LINK_NOT_FOUND, LINK_EXPIRED, LINK_REVOKED).
 * Dispatched when the API returns these errors on any widget endpoint.
 */
export const SESSION_EXPIRED_EVENT = "raas-widgets-flow-fund-request-session-expired";

export const MAINTENANCE_EVENT = "raas-widgets-flow-fund-request-maintenance";

export interface MaintenanceStatus {
  active: boolean;
  title?: string;
  body?: string;
  supportEmail?: string;
  supportWhatsappNumber?: string;
}

/**
 * True when the failure means the link itself died (expired / revoked / gone).
 * `useSessionExpiredHandler` already routes those to the explanatory screen, so
 * callers must not overwrite it with their own generic error copy.
 */
export const isDeadLinkError = (error: unknown): boolean => {
  if (!axios.isAxiosError(error)) return false;
  const code = error.response?.data?.error?.code;
  return DEAD_LINK_CODES.includes(code) || error.response?.status === 410;
};

/**
 * Current link ID for API requests. Set after a successful claim.
 */
let currentLinkId: string | null = null;

export type ConsumerClerkTokenGetter = () => Promise<string | null>;

let consumerClerkTokenGetter: ConsumerClerkTokenGetter | null = null;

/** Injected by ClerkConsumerRoot so account APIs send the Consumer JWT. */
export const setConsumerClerkTokenGetter = (
  getter: ConsumerClerkTokenGetter | null
) => {
  consumerClerkTokenGetter = getter;
};

export const getConsumerClerkToken = async (): Promise<string | null> => {
  if (!consumerClerkTokenGetter) return null;
  return consumerClerkTokenGetter();
};

/**
 * Set the current link ID for API requests (adds the x-link-id header).
 */
export const setWidgetLinkId = (linkId: string | null) => {
  currentLinkId = linkId;
};

export const getWidgetLinkId = (): string | null => currentLinkId;

export const probeLinkSession = async (
  linkId: string,
): Promise<{ expiresAt: string | null }> => {
  const { data } = await widgetApi.get(`/widget/links/${linkId}/session`, {
    raasSilentOffline: true,
  });
  const expiresAt = data?.data?.expiresAt;
  return { expiresAt: typeof expiresAt === "string" ? expiresAt : null };
};

export const fetchMaintenanceStatus = async (
  linkId: string,
  locale: string,
): Promise<MaintenanceStatus> => {
  const { data } = await widgetApi.get(
    `/widget/links/${linkId}/maintenance`,
    { params: { locale }, raasSilentOffline: true },
  );
  const payload = data?.data;
  if (!payload?.active) return { active: false };
  return {
    active: true,
    title: typeof payload.title === "string" ? payload.title : undefined,
    body: typeof payload.body === "string" ? payload.body : undefined,
    supportEmail:
      typeof payload.supportEmail === "string" ? payload.supportEmail : undefined,
    supportWhatsappNumber:
      typeof payload.supportWhatsappNumber === "string"
        ? payload.supportWhatsappNumber
        : undefined,
  };
};

export const dispatchSessionExpiredEvent = (detail: {
  code: string;
  details?: unknown;
}): void => {
  window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT, { detail }));
};

/**
 * Link id for endpoints that carry it in the path. `currentLinkId` is empty on
 * the first render after a full page load — the Clerk SSO round-trip returns to
 * `/auth-page` before the session bootstrap restores it — so resolve it the same
 * way the `x-link-id` header does. Sending a placeholder makes the backend
 * guard prefer the path, answer `LINK_NOT_FOUND`, and the widget then reports a
 * live link as expired.
 */
export const resolvePathLinkId = (): string | null => {
  const linkId = resolveXLinkIdHeader({
    urlLinkId: resolveUrlLinkId(),
    currentLinkId,
    persistedLinkId: useSessionStore.getState().session.data?.linkId ?? null,
  });
  if (linkId && linkId !== currentLinkId) {
    setWidgetLinkId(linkId);
  }
  return linkId;
};

function extractRequestLinkId(req: { headers?: unknown; url?: string } | undefined): string | null {
  if (!req) return null;
  const headers = req.headers as Record<string, unknown> | undefined;
  const header = headers?.["x-link-id"] ?? headers?.["X-Link-Id"];
  if (typeof header === "string" && header.length > 0) return header;
  const match = (req.url ?? "").match(/\/widget\/links\/([^/?]+)/);
  return match?.[1] ?? null;
}

export function shouldBroadcastSessionExpired(req: {
  headers?: unknown;
  url?: string;
} | undefined): boolean {
  if (useSessionStore.getState().session.status === RequestStatus.LOADING) {
    return false;
  }
  const reqLinkId = extractRequestLinkId(req);
  const urlLinkId = resolveUrlLinkId();
  if (reqLinkId && urlLinkId && reqLinkId !== urlLinkId) {
    return false;
  }
  return true;
}

/**
 * Axios instance for authenticated widget API calls (x-link-id header).
 */
const createWidgetApiInstance = (): AxiosInstance => {
  const instance = axios.create({
    baseURL: config.API_BASE_URL,
  });

  instance.interceptors.request.use(async (requestConfig) => {
    // Bypass a disk-cached 410 on header-only GETs (catalog URL has no linkId).
    const method = (requestConfig.method ?? "get").toLowerCase();
    if (method === "get") {
      requestConfig.headers["Cache-Control"] = "no-cache";
      requestConfig.headers["Pragma"] = "no-cache";
    }
    const urlLinkId = resolveUrlLinkId();
    const persisted =
      useSessionStore.getState().session.data?.linkId ?? null;
    if (urlLinkId && currentLinkId !== urlLinkId) {
      setWidgetLinkId(urlLinkId);
    }
    const header = resolveXLinkIdHeader({
      urlLinkId,
      currentLinkId,
      persistedLinkId: persisted,
    });
    if (header) {
      requestConfig.headers["x-link-id"] = header;
    }
    // Bypass ngrok's free-tier interstitial ("ERR_NGROK_6024") so XHR calls
    // reach the backend instead of getting the HTML splash page. Only needed
    // when the API is tunneled through ngrok (dev); harmless otherwise.
    if (config.API_BASE_URL.includes("ngrok")) {
      requestConfig.headers["ngrok-skip-browser-warning"] = "true";
    }
    if (!requestConfig.headers.Authorization && consumerClerkTokenGetter) {
      const token = await consumerClerkTokenGetter();
      if (token) {
        requestConfig.headers.Authorization = `Bearer ${token}`;
      }
    }
    return requestConfig;
  });

  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      const errorCode = error?.response?.data?.error?.code;
      const statusCode = error?.response?.status;

      if (errorCode === LINK_ERROR_CODE.SERVICE_UNAVAILABLE_MAINTENANCE) {
        window.dispatchEvent(
          new CustomEvent(MAINTENANCE_EVENT, {
            detail: error?.response?.data?.error?.details,
          }),
        );
      }

      if (
        (DEAD_LINK_CODES.includes(errorCode) || statusCode === 410) &&
        shouldBroadcastSessionExpired(error?.config)
      ) {
        window.dispatchEvent(
          new CustomEvent(SESSION_EXPIRED_EVENT, {
            detail: {
              code: errorCode || LINK_ERROR_CODE.LINK_EXPIRED,
              details: error?.response?.data?.error?.details,
            },
          })
        );
      }

      if (
        isAxiosNetworkFailure(error) &&
        error.config?.raasSilentOffline !== true
      ) {
        useOfflineStore.getState().reportOffline();
      }

      return Promise.reject(error);
    }
  );

  return instance;
};

export const widgetApi = createWidgetApiInstance();

export interface NotificationPreferenceChannel {
  channel: "in_app" | "email" | "sms";
  enabled: boolean;
}

export interface SmsConsentEntry {
  id: string;
  introMarkdown: string;
  detailTitle?: string;
  detailMarkdown: string;
}

export interface SmsConsentData {
  available: boolean;
  version?: string;
  maskedPhone?: string;
  current: "accepted" | "declined" | "none";
  language?: string;
  entries: SmsConsentEntry[];
}

export const getSmsConsent = async (
  linkId: string,
  lang: string,
): Promise<SmsConsentData> => {
  const { data } = await widgetApi.get(`/widget/links/${linkId}/sms-consent`, {
    params: { lang },
  });
  return data.data;
};

/** Records Accept or Not now. Returns the refreshed preference map. */
export const decideSmsConsent = async (
  linkId: string,
  input: {
    decision: "accepted" | "declined";
    version: string;
    language: string;
  },
): Promise<NotificationPreferenceChannel[]> => {
  const { data } = await widgetApi.post(
    `/widget/links/${linkId}/sms-consent`,
    input,
  );
  return data.data.channels;
};

export const getNotificationPreferences = async (
  linkId: string,
): Promise<NotificationPreferenceChannel[]> => {
  const { data } = await widgetApi.get(
    `/widget/links/${linkId}/notification-preferences`,
  );
  return data.data.channels;
};

export const saveNotificationPreferences = async (
  linkId: string,
  channels: NotificationPreferenceChannel[],
): Promise<NotificationPreferenceChannel[]> => {
  const { data } = await widgetApi.put(
    `/widget/links/${linkId}/notification-preferences`,
    { channels },
  );
  return data.data.channels;
};

export const saveContactPhone = async (
  linkId: string,
  phone: string,
  locale: string
): Promise<{ hasPhoneNumber: boolean; phoneCountry?: string }> => {
  const { data } = await widgetApi.put(
    `/widget/links/${linkId}/contact/phone`,
    { phone, locale }
  );
  return data.data;
};


/**
 * Device info to send with the claim request.
 */
export interface IDeviceInfo {
  userAgent?: string;
  language?: string;
  timezone?: string;
}

/**
 * Claim a magic link by linkId.
 * POST /widget/links/:linkId/claim
 *
 * The response contains the partner configuration — including the theme that
 * the widget must apply.
 */
export const claimLink = async (
  linkId: string,
  deviceInfo?: IDeviceInfo,
  lang?: string,
  seonSession?: string
): Promise<IWidgetSession> => {
  const apiBaseUrl = config.API_BASE_URL;

  try {
    const { data } = await axios.post(
      `${apiBaseUrl}/widget/links/${linkId}/claim`,
      {
        deviceInfo,
        ...(seonSession ? { seonSession } : {}),
      },
      {
        params: lang ? { lang } : undefined,
        ...(apiBaseUrl.includes("ngrok") && {
          headers: { "ngrok-skip-browser-warning": "true" },
        }),
      }
    );

    return {
      sessionId: data.data.sessionId,
      linkId: data.data.linkId,
      token: data.data.token,
      status: data.data.status,
      expiresAt: data.data.expiresAt,
      callbackUrl: data.data.callbackUrl,
      partner: {
        partnerId: data.data.partner.partnerId,
        name: data.data.partner.name,
        primaryColor: data.data.partner.primaryColor,
        theme: data.data.partner.theme,
        supportEmail: data.data.partner.supportEmail,
        supportWhatsappNumber: data.data.partner.supportWhatsappNumber,
        sessionExpiredRedirectUrl: data.data.partner.sessionExpiredRedirectUrl,
      },
      userData: data.data.userData,
      user: data.data.user,
      config: data.data.config,
      language: data.data.language,
      flavor: data.data.flavor,
      themeMode: data.data.themeMode,
      termsAccepted: data.data.termsAccepted,
      termsAcceptedAt: data.data.termsAcceptedAt,
      terms: data.data.terms,
    };
  } catch (error: any) {
    const status = error?.response?.status;
    const apiError = error?.response?.data?.error;
    const code = resolveLinkErrorCode(status, apiError?.code);

    // The message is only a diagnostic fallback: the error screen resolves the
    // user-facing copy from `code` via i18n.
    throw new LinkClaimError(
      apiError?.message || `Failed to claim link (${code})`,
      code,
      status,
      apiError?.details,
    );
  }
};

export type ConsumerOccupancy =
  | "acquired"
  | "already_occupant"
  | "other_occupant";

export interface IBindConsumerIdentityResponse {
  sessionId?: string;
  linkId: string;
  referenceId: string;
  consumerClerkOrgId?: string;
  isOccupant: boolean;
  occupancy: ConsumerOccupancy;
  user: {
    userId: string;
    referenceId: string;
    cipStatus?: string;
    /** Verified CIP given name. Absent until CIP is verified. */
    firstName?: string;
    blocked: boolean;
    suspended: boolean;
    hasPhoneNumber?: boolean;
    /** ISO2 from the user address. Absent when unknown. */
    country?: string;
  };
  termsAccepted: boolean;
  termsAcceptedAt?: string;
  novu?: INovuSession;
}

export interface INovuSession {
  applicationIdentifier: string;
  subscriberId: string;
  subscriberHash: string;
  contextHash: string;
  /**
   * Partner in-app channel. Missing on a bind from before this field
   * means keep the bell.
   */
  inAppEnabled?: boolean;
  context: {
    tenant: {
      id: string;
      data: {
        name: string;
        logoUrl: string;
        primaryColor: string;
        supportEmail: string;
      };
    };
    widget: {
      id: string;
    };
  };
}

/**
 * Bind the Consumer Clerk session to this magic link.
 * POST /widget/links/:linkId/bind-consumer-identity
 */
export const bindConsumerIdentity = async (
  clerkToken: string
): Promise<IBindConsumerIdentityResponse> => {
  const linkId = resolvePathLinkId();
  if (!linkId) {
    throw new Error("missing_link_id");
  }
  const { data } = await widgetApi.post(
    `/widget/links/${linkId}/bind-consumer-identity`,
    {},
    {
      headers: { Authorization: `Bearer ${clerkToken}` },
    }
  );
  return data.data;
};

/**
 * Record the Terms & Conditions / E-Sign acceptance for the current user.
 * Persisted at the user level by the backend so the same user is not prompted
 * again on future links.
 * POST /widget/links/:linkId/accept-terms
 */
export interface IAcceptTermsResponse {
  termsAccepted: boolean;
  termsAcceptedAt?: string;
  termsAcceptedVersion?: string;
}

export const acceptTerms = async (
  linkId: string,
  options?: {
    version?: string;
    documents?: string[];
    acceptedEntryIds?: string[];
    language?: string;
  }
): Promise<IAcceptTermsResponse> => {
  const deviceInfo: IDeviceInfo = {
    userAgent: navigator.userAgent,
    language: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };

  const { data } = await widgetApi.post(
    `/widget/links/${linkId}/accept-terms`,
    {
      version: options?.version,
      documents: options?.documents,
      acceptedEntryIds: options?.acceptedEntryIds,
      language: options?.language ?? i18n.language,
      deviceInfo,
    }
  );

  return data.data;
};

export interface ILegalDocumentResponse {
  html: string;
}

/**
 * Fetch a legal document (E-Sign, Terms, etc.) as ready-to-render HTML.
 * GET /widget/legal/:doc/:lang
 */
export const getLegalDocument = async (
  doc: string,
  lang: string
): Promise<ILegalDocumentResponse> => {
  const { data } = await widgetApi.get(`/widget/legal/${doc}/${lang}`);
  return data.data;
};

/**
 * Fetch the partner Terms & Conditions for the link in a specific language.
 * The backend serves manually authored per-language content and falls back to
 * English when the requested language is unavailable.
 * GET /widget/links/:linkId/terms?lang=xx
 */
export const getTerms = async (
  linkId: string,
  lang?: string
): Promise<ITermsBlock | null> => {
  const { data } = await widgetApi.get(`/widget/links/${linkId}/terms`, {
    params: lang ? { lang } : undefined,
  });
  return data.data?.terms ?? null;
};

// ============================================
// Pricing API (Pre-quote + binding Quote)
//
// Two-step pricing, mirroring the m2m widget:
//   1. POST /widget/pre-quote      -> indicative SKUs (capabilities) for the corridor
//   2. POST /widget/operations/quote -> binding signed quote for a chosen SKU
// ============================================

/** Which leg the `amount` is pinned to. */
export type QuoteAnchor = "send" | "receive";

/** What the backend should vary when fanning out the pre-quote. */
export type PreQuoteVary = "payer" | "fundingMethod";

/** Sections to return on a pre-quote response. */
export type PreQuoteInclude = "options" | "summary" | "facets";

/**
 * Indicative pre-quote request. The widget provides the corridor + amount +
 * currencies and (optionally) the payout/funding method; the backend fans out
 * across every quotable capability and returns a ranked list of options.
 * POST /widget/pre-quote
 */
export interface IPreQuoteRequest {
  originCountryCode: string;
  destinationCountryCode: string;
  /** Interpreted per `anchor`: source ccy for `send`, destination for `receive`. */
  amount: number;
  /** Defaults to `send` server-side. */
  anchor?: QuoteAnchor;
  destinationCurrency: string;
  /** Defaults to `USD` server-side. */
  sourceCurrency?: string;
  fundingMethod?: string;
  payoutMethod?: string;
  /**
   * Pin the receive network before `vary` collapse so cheapest-per-funding
   * is computed within that network (with server-side payer equivalence).
   */
  payerCode?: string;
  /** Defaults to `payer` server-side. */
  vary?: PreQuoteVary;
  include?: PreQuoteInclude[];
}

/**
 * A single quotable capability (SKU) returned by the pre-quote, with its
 * indicative economics. `capabilityKey` is the binding identifier the widget
 * pins when requesting the signed quote.
 */
export interface IEstimateOption {
  capabilityKey: string;
  corridorCode: string;
  payerCode: string | null;
  payoutMethod: string;
  fundingMethod: string;
  sendAmount: number;
  receiveAmount: number;
  customerFee: number;
  customerFxRate: number;
  sourceCurrency: string;
  destinationCurrency: string;
}

export interface IEstimateRange {
  min: number;
  max: number;
  avg: number;
}

export interface IEstimateSummary {
  count: number;
  best?: IEstimateOption;
  worst?: IEstimateOption;
  rankingMetric: "receiveAmount" | "sendAmount";
  receiveAmount?: IEstimateRange;
  sendAmount?: IEstimateRange;
  asOf: string;
  indicative: true;
}

export interface IEstimateFacetBest {
  capabilityKey?: string;
  sendAmount?: number;
  receiveAmount?: number;
  customerFee?: number;
  customerFxRate?: number;
  corridorCode: string;
}

export interface IEstimateFacets {
  fundingMethods?: Array<{ fundingMethod: string; best: IEstimateFacetBest }>;
  payoutMethods?: Array<{ payoutMethod: string; best: IEstimateFacetBest }>;
  payers?: Array<{ payerCode: string; best: IEstimateFacetBest }>;
}

export interface IEstimateResult {
  options: IEstimateOption[];
  summary: IEstimateSummary;
  facets?: IEstimateFacets;
}

/**
 * Get an indicative (non-binding) pre-quote across all quotable options.
 * POST /widget/pre-quote
 */
export const getPreQuote = async (
  input: IPreQuoteRequest
): Promise<IEstimateResult> => {
  // Pre-quote fans out across many payers; keep headroom over the default 15s
  // client timeout so a cold FX desk / wide corridor doesn't abort mid-flight.
  const { data } = await widgetApi.post(`/widget/pre-quote`, input, {
    timeout: 40_000,
  });
  return data.data;
};

export interface IResolveLimitsRequest {
  corridorCode: string;
  originCountryCode?: string;
  destinationCountryCode?: string;
  payoutMethod?: string;
  payerCode?: string;
  fundingMethod?: string;
  sendCurrency?: string;
}

export interface IAmountBound {
  minSend: number | null;
  maxSend: number | null;
  currency: string | null;
  bounded: boolean;
}

/**
 * Resolve min/max send amount for a SKU + funding method.
 * POST /widget/limits/resolve
 */
export const resolveAmountLimits = async (
  input: IResolveLimitsRequest
): Promise<IAmountBound> => {
  const { data } = await widgetApi.post(`/widget/limits/resolve`, input);
  return data.data.amount;
};

export interface IQuoteRequest {
  sourceCountry: string;
  destinationCountry: string;
  /** Funding method (canonical catalog vocabulary, UPPER_SNAKE). */
  fundingMethod: string;
  /** Payout method (canonical catalog vocabulary, UPPER_SNAKE). */
  payoutMethod: string;
  /**
   * Exact capability (SKU) chosen from the pre-quote options. The binding quote
   * is priced + frozen against this exact SKU by the pricing engine.
   */
  capabilityKey: string;
  /** Which leg `amount` is pinned to. Defaults to `send` server-side. */
  anchor?: QuoteAnchor;
  amount: number;
  currency: string;
  /** Destination currency; required when `anchor` is `receive`. */
  destinationCurrency?: string;
  cardId?: string;
}

export interface IQuoteResponse {
  quoteId: string;
  sendAmount: number;
  receiveAmount: number;
  exchangeRate: number;
  fee?: number;
  totalFee?: number;
  totalCost: number;
  sourceCurrency: string;
  destinationCurrency: string;
  fundingMethod: string;
  payoutMethod: string;
  expiresAt: string;
  signedQuote: string;
  cardId?: string;
  /** Capability + anchor the quote was priced against (stamped client-side). */
  capabilityKey?: string;
  anchor?: QuoteAnchor;
}

/**
 * Get a binding signed quote for a money transfer.
 * POST /widget/operations/quote
 */
export const getQuote = async (
  request: IQuoteRequest,
  options?: { raasSilentOffline?: boolean },
): Promise<IQuoteResponse> => {
  const { data } = await widgetApi.post(`/widget/operations/quote`, request, {
    raasSilentOffline: options?.raasSilentOffline,
  });

  // Stamp the request's capability + anchor onto the response so downstream
  // consumers (e.g. QUOTE_EXPIRED refresh) can re-price the same SKU.
  return {
    ...data.data,
    capabilityKey: request.capabilityKey,
    anchor: request.anchor,
  };
};

// ============================================
// Payment Methods API (VGS proxy tokenization)
//
// Two-step card registration, mirroring the m2m widget:
//   1. Tokenize the raw PAN/CVV through the VGS forward proxy (the sensitive
//      data never touches our backend in the clear).
//   2. Register the tokenized instrument + billing address against the backend,
//      which validates it with RAAS (card pull) before persisting it.
// ============================================

import type {
  ICardPaymentMethod,
  IBankAccountPaymentMethod,
  IAllPaymentMethods,
  IAddCardPaymentMethodRequest,
  IPreValidateCardRequest,
  IPreValidateCardResult,
  ICardUsageReadinessInput,
  ICardUsageReadinessResult,
  ICardProvisionInput,
  ICardProvisionResult,
  IAddDestinationCardRequest,
  IPreValidateDestinationCardRequest,
  IPreValidateDestinationCardResult,
  IDestinationCardProvisionResult,
  IAddBankAccountRequest,
} from "../interfaces/payment-method";

type VgsAliasField = "card_number" | "card_cvc";

type VgsEchoBody = {
  json?: Partial<Record<VgsAliasField, string>>;
} & Partial<Record<VgsAliasField, string>>;

const pickAlias = (
  data: VgsEchoBody | null | undefined,
  field: VgsAliasField
): string | undefined => {
  const fromJson = data?.json?.[field];
  if (typeof fromJson === "string" && fromJson.length > 0) return fromJson;
  const top = data?.[field];
  if (typeof top === "string" && top.length > 0) return top;
  return undefined;
};

const isVgsAlias = (value: string | undefined): value is string =>
  typeof value === "string" && value.startsWith("tok_");

/**
 * Tokenize card data via the VGS inbound route.
 * POST https://{proxyHost}/post with Content-Type exactly application/json.
 * Body is only { card_number, card_cvc }.
 */
export const tokenizeCard = async (
  pan: string,
  cvv: string
): Promise<{ numberToken: string; cvvToken: string }> => {
  const vgsProxyUrl = await vgsPostUrl();

  const { data } = await axios.post<VgsEchoBody>(
    vgsProxyUrl,
    { card_number: pan, card_cvc: cvv },
    { headers: { "Content-Type": "application/json" } }
  );

  const numberToken = pickAlias(data, "card_number");
  const cvvToken = pickAlias(data, "card_cvc");
  if (!isVgsAlias(numberToken) || !isVgsAlias(cvvToken)) {
    throw new Error("VGS_TOKENIZE_FAILED");
  }
  return { numberToken, cvvToken };
};

/** POST /widget/links/:linkId/payment-methods/cards/pre-validate */
export const preValidateCardPaymentMethod = async (
  linkId: string,
  card: IPreValidateCardRequest
): Promise<IPreValidateCardResult> => {
  const { data } = await widgetApi.post<
    IPreValidateCardResult | { success: boolean; data: IPreValidateCardResult }
  >(`/widget/links/${linkId}/payment-methods/cards/pre-validate`, card, {
    timeout: 40_000,
  });

  if (data && typeof data === "object" && "validationId" in data) {
    return data;
  }
  return (data as { data: IPreValidateCardResult }).data;
};

/** Tokenize a volatile CVV via VGS (no PAN). */
export const tokenizeCvv = async (cvv: string): Promise<string> => {
  const vgsProxyUrl = await vgsPostUrl();
  const { data } = await axios.post<VgsEchoBody>(
    vgsProxyUrl,
    { card_cvc: cvv },
    { headers: { "Content-Type": "application/json" } }
  );
  const token = pickAlias(data, "card_cvc");
  if (!isVgsAlias(token)) throw new Error("CVV_TOKENIZE_FAILED");
  return token;
};

/** POST /widget/links/:linkId/payment-methods/cards/:cardId/usage-readiness */
export const cardUsageReadiness = async (
  linkId: string,
  cardId: string,
  input: ICardUsageReadinessInput
): Promise<ICardUsageReadinessResult> => {
  const { data } = await widgetApi.post<{
    success: boolean;
    data: ICardUsageReadinessResult;
  }>(
    `/widget/links/${linkId}/payment-methods/cards/${cardId}/usage-readiness`,
    input
  );
  return data.data;
};

/** POST /widget/links/:linkId/payment-methods/cards/:cardId/provision */
export const provisionCard = async (
  linkId: string,
  cardId: string,
  input: ICardProvisionInput
): Promise<ICardProvisionResult> => {
  const { data } = await widgetApi.post<{
    success: boolean;
    data: ICardProvisionResult;
  }>(`/widget/links/${linkId}/payment-methods/cards/${cardId}/provision`, input, {
    timeout: 60_000,
  });
  return data.data;
};

/** PAN-only VGS tokenize fallback for register when numberToken is missing. */
export const tokenizeDestinationCard = async (
  pan: string
): Promise<{ numberToken: string }> => {
  const vgsProxyUrl = await vgsPostUrl();
  const { data } = await axios.post<VgsEchoBody>(
    vgsProxyUrl,
    { card_number: pan },
    { headers: { "Content-Type": "application/json" } }
  );
  const numberToken = pickAlias(data, "card_number");
  if (!isVgsAlias(numberToken)) throw new Error("VGS_TOKENIZE_FAILED");
  return { numberToken };
};

/** POST /widget/links/:linkId/payment-methods/destination-cards/pre-validate */
export const preValidateDestinationCard = async (
  linkId: string,
  card: IPreValidateDestinationCardRequest
): Promise<IPreValidateDestinationCardResult> => {
  const { data } = await widgetApi.post<{
    success: boolean;
    data: IPreValidateDestinationCardResult;
  }>(
    `/widget/links/${linkId}/payment-methods/destination-cards/pre-validate`,
    card,
    { timeout: 40_000 }
  );
  return data.data;
};

/** POST /widget/links/:linkId/payment-methods/destination-cards */
export const addDestinationCardPaymentMethod = async (
  linkId: string,
  card: IAddDestinationCardRequest
): Promise<ICardPaymentMethod> => {
  const { data } = await widgetApi.post(
    `/widget/links/${linkId}/payment-methods/destination-cards`,
    card
  );
  return data.data;
};

/** POST /widget/links/:linkId/payment-methods/destination-cards/:cardId/usage-readiness */
export const destinationCardUsageReadiness = async (
  linkId: string,
  cardId: string,
  input: ICardUsageReadinessInput
): Promise<ICardUsageReadinessResult> => {
  const { data } = await widgetApi.post<{
    success: boolean;
    data: ICardUsageReadinessResult;
  }>(
    `/widget/links/${linkId}/payment-methods/destination-cards/${cardId}/usage-readiness`,
    input
  );
  return data.data;
};

/** POST /widget/links/:linkId/payment-methods/destination-cards/:cardId/provision */
export const provisionDestinationCard = async (
  linkId: string,
  cardId: string,
  input: ICardUsageReadinessInput
): Promise<IDestinationCardProvisionResult> => {
  const { data } = await widgetApi.post<{
    success: boolean;
    data: IDestinationCardProvisionResult;
  }>(
    `/widget/links/${linkId}/payment-methods/destination-cards/${cardId}/provision`,
    input,
    { timeout: 60_000 }
  );
  return data.data;
};

/** POST /widget/links/:linkId/payment-methods/bank-accounts */
export const addBankAccountPaymentMethod = async (
  linkId: string,
  account: IAddBankAccountRequest
): Promise<IBankAccountPaymentMethod> => {
  const { data } = await widgetApi.post(
    `/widget/links/${linkId}/payment-methods/bank-accounts`,
    account
  );
  return data.data;
};

/**
 * Register a tokenized card as a funding payment method.
 * POST /widget/links/:linkId/payment-methods/cards
 */
export const addCardPaymentMethod = async (
  linkId: string,
  card: IAddCardPaymentMethodRequest
): Promise<ICardPaymentMethod> => {
  const { data } = await widgetApi.post(
    `/widget/links/${linkId}/payment-methods/cards`,
    card
  );
  return data.data;
};

/**
 * Remove a card payment method.
 * DELETE /widget/links/:linkId/payment-methods/cards/:cardId
 */
export const removeCardPaymentMethod = async (
  linkId: string,
  cardId: string
): Promise<void> => {
  await widgetApi.delete(
    `/widget/links/${linkId}/payment-methods/cards/${cardId}`
  );
};

/**
 * Get all payment methods for the user (cards, bank accounts, etc.).
 * GET /widget/links/:linkId/payment-methods
 */
export const getAllPaymentMethods = async (
  linkId: string
): Promise<IAllPaymentMethods> => {
  const { data } = await widgetApi.get(
    `/widget/links/${linkId}/payment-methods`
  );
  const payload = data.data as IAllPaymentMethods;
  return {
    ...payload,
    cards: (payload.cards ?? []).map((c) => ({
      ...c,
      brand: (c.brand || "").toUpperCase(),
    })),
  };
};

// ============================================
// CIP Readiness API
//
// Before a transfer can be committed the backend decides whether the user must
// still complete identity verification (CIP). It also lazily provisions the
// Kentucky CIP ticket so the widget can launch the embedded SDK with it.
// ============================================

/**
 * CIP readiness response.
 *
 * `shouldRunCip` is the decision flag; when true the widget must run the
 * Kentucky SDK against the freshly-provisioned `cipTicketId` before creating
 * the operation. When false the transfer can proceed straight to commit.
 */
export interface ICipReadinessResponse {
  shouldRunCip: boolean;
  reason: string;
  cipTicketUrl: string | null;
  cipTicketId: string | null;
  missingFields?: string[];
}

/**
 * Check if the user needs to complete CIP verification before transferring.
 * GET /widget/links/:linkId/cip-readiness
 */
export const checkCipReadiness = async (
  linkId: string
): Promise<ICipReadinessResponse> => {
  const { data } = await widgetApi.get(
    `/widget/links/${linkId}/cip-readiness`
  );
  return data.data;
};

// ============================================
// Operations Create API (the "comanda")
//
// Commits the signed quote into a real money-transfer operation once pricing
// and (if required) CIP are resolved.
// ============================================

/**
 * Create operation request.
 *
 * `fundingPaymentMethodId` is required for funding methods that consume a
 * stored instrument (DEBIT_CARD, CREDIT_CARD, ACH, RTP, BANK_BALANCE), and
 * omitted for CASH_DEPOSIT (paid in person; no stored instrument).
 */
export interface ICreateOperationRequest {
  signedQuote: string;
  fundingPaymentMethodId?: string;
  /** Confirm-time VGS CVV alias when usage-readiness asked for CVV. Never persisted client-side. */
  fundingCvvToken?: string;
  payoutPaymentMethodId?: string;
  notes?: string;
  /** Optional SEON Device Intelligence session captured at commit time. */
  seonSession?: string;
  /**
   * Recipient party. In the fund-request flow the recipient is the REQUESTER
   * of the money (the person who minted the link), so the name comes from the
   * claim's `userData` — building it here keeps the business-case recipe in
   * the flow (the backend persists the snapshot verbatim and projects it for
   * OFAC screening; it never infers identity).
   */
  recipient?: {
    name: {
      firstName: string;
      lastName: string;
      middleName?: string;
      secondLastName?: string;
    };
    address?: {
      countryISO2?: string;
      /** Full ISO 3166-2 subdivision code (e.g. `MX-JAL`). */
      state?: string;
      city?: string;
      line1?: string;
      zip?: string;
    };
    phone?: string;
  };
}

/**
 * Operation response.
 */
export interface IOperationResponse {
  operationId: string;
  quoteId: string;
  status: string; // 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled'
  /** Coarse reason; `provider_rejected` when the corridor permanently rejected. */
  statusReason?: string;
  sendAmount: number;
  receiveAmount: number;
  exchangeRate: number;
  fee?: number;
  fundingFee?: number;
  transactionFee?: number;
  taxFee?: number;
  totalFee?: number;
  totalCost: number;
  sourceCurrency: string;
  destinationCurrency: string;
  fundingPaymentMethodId?: string;
  payoutPaymentMethodId?: string;
  notes?: string;
  /** Canonical funding method (e.g. DEBIT_CARD, ACH, CASH_DEPOSIT). */
  fundingMethod?: string;
  /** Push-funding sub-status (awaiting_funding | barcode_issued | funded | expired). */
  fundingStatus?: string;
  /** Recipient party snapshot from operation creation (requester in this flow). */
  recipient?: ICreateOperationRequest["recipient"];
  /** Partner User.referenceId of who receives the funds (the requester). */
  recipientReferenceId?: string;
  /** Canonical corridor references (payout code, tracking ref). */
  references?: Array<{
    type: string;
    value: string;
    label?: Record<string, string>;
    presentation: string;
    issuedAt?: string;
  }>;
  /** Canonical documents the corridor can issue live (PDF receipts). */
  documents?: Array<{
    kind: string;
    mediaType: string;
    freshness: "LIVE" | "IMMUTABLE";
    availability: "AVAILABLE" | "PENDING" | "UNAVAILABLE";
    locales?: string[];
  }>;
  createdAt: string;
  cancellation?: {
    allowed: boolean;
    mode?: "void" | "request";
    expiresAt?: string;
    unavailableReason?: string;
    requestedAt?: string;
  };
}

/**
 * Create a money transfer operation.
 * POST /widget/links/:linkId/operations
 */
export const createOperation = async (
  linkId: string,
  request: ICreateOperationRequest
): Promise<IOperationResponse> => {
  const seonSession =
    request.seonSession ?? (await getDeviceSession()) ?? undefined;
  const { data } = await widgetApi.post(
    `/widget/links/${linkId}/operations`,
    {
      ...request,
      ...(seonSession ? { seonSession } : {}),
    }
  );
  return data.data;
};

/** Pagination metadata returned when the operations list is paginated. */
export interface IPaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasMore: boolean;
}

/** One page of operations (used for infinite scroll on the home screen). */
export interface IOperationsPage {
  items: IOperationResponse[];
  pagination?: IPaginationMeta;
}

/**
 * List operations for the user (most recent first).
 * GET /widget/links/:linkId/operations
 *
 * When `limit` is provided the backend paginates and returns `pagination`
 * metadata alongside the page of items, enabling lazy/infinite scroll.
 */
export const listOperations = async (
  linkId: string,
  params?: { page?: number; limit?: number }
): Promise<IOperationsPage> => {
  const { data } = await widgetApi.get(
    `/widget/links/${linkId}/operations`,
    params ? { params } : undefined
  );
  return { items: data.data, pagination: data.pagination };
};

/**
 * Get a single operation by ID (used by the receipt/detail screen).
 * GET /widget/links/:linkId/operations/:operationId
 */
export const getOperation = async (
  linkId: string,
  operationId: string,
  options?: { raasSilentOffline?: boolean },
): Promise<IOperationResponse> => {
  const { data } = await widgetApi.get(
    `/widget/links/${linkId}/operations/${operationId}`,
    { raasSilentOffline: options?.raasSilentOffline },
  );
  return data.data;
};

/**
 * Issue a live corridor document (PDF receipt) for the operation.
 * GET /widget/links/:linkId/operations/:operationId/documents/:kind
 *
 * Filename is derived client-side: CORS does not expose Content-Disposition.
 */
export const fetchOperationDocument = async (
  linkId: string,
  operationId: string,
  kind: string
): Promise<Blob> => {
  const { data } = await widgetApi.get(
    `/widget/links/${linkId}/operations/${operationId}/documents/${kind}`,
    { responseType: "blob" }
  );
  return data;
};

export interface ICancelOperationResponse {
  operationId: string;
  status: string;
  cancellationReason: string;
  mode?: "void" | "request";
  corridorOutcome?: "ok" | "failed" | "not_applicable";
}

/** POST /widget/links/:linkId/discard — closes the money request; the link stays claimable. */
export const discardFundRequest = async (
  linkId: string
): Promise<{ requestId: string; status: "cancelled" }> => {
  const { data } = await widgetApi.post(`/widget/links/${linkId}/discard`);
  return data.data;
};

/** POST /widget/links/:linkId/operations/:operationId/cancel */
export const cancelOperation = async (
  linkId: string,
  operationId: string
): Promise<ICancelOperationResponse> => {
  const { data } = await widgetApi.post(
    `/widget/links/${linkId}/operations/${operationId}/cancel`
  );
  return data.data;
};

export type CatalogEffectiveStatus = "AVAILABLE" | "UNAVAILABLE";

/** Catalog payer row (`/widget/catalog/payers`). */
export interface IPayerCapability {
  payerCode: string;
  displayName: string;
  countryCode: string;
  payoutMethods: string[];
  supportedByCorridors: string[];
  effectiveStatus: CatalogEffectiveStatus;
}

export interface ICatalogPayersInput {
  originCountryCode?: string;
  destinationCountryCode: string;
  payoutMethod?: string;
  /** Include payers the corridor cannot currently quote (default: false). */
  includeUnavailable?: boolean;
}

/**
 * Payers of a destination + payout method, with their canonical `displayName`.
 * The claim pins a payer code (e.g. `GT_BANCO_GYT_CASH`); this is the only
 * source of a human-readable name for it.
 * GET /widget/catalog/payers
 */
export const getCatalogPayers = async (
  input: ICatalogPayersInput
): Promise<IPayerCapability[]> => {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(input)) {
    if (value === undefined || value === null || value === "") continue;
    params.set(key, String(value));
  }
  const { data } = await widgetApi.get(
    `/widget/catalog/payers?${params.toString()}`
  );
  return data.data.items;
};

/** Result of minting a cash-deposit operation_payment link. */
export interface ICashDepositLink {
  /** Full URL to launch the cash-deposit widget flow. */
  url: string;
  linkId: string;
}

/**
 * Mint a cash-deposit operation_payment link for an awaiting-funding operation.
 * POST /widget/links/:linkId/operations/:operationId/cash-deposit-link
 *
 * `callbackUrl` is where the cash-deposit flow returns the user from its success
 * screen (typically this widget's home).
 */
export const createCashDepositLink = async (
  linkId: string,
  operationId: string,
  callbackUrl: string
): Promise<ICashDepositLink> => {
  const { data } = await widgetApi.post(
    `/widget/links/${linkId}/operations/${operationId}/cash-deposit-link`,
    { callbackUrl }
  );
  return data.data;
};

/** Chain from GET /widget/operations/funding-locations (same as cash-deposit). */
export interface IFundingMerchant {
  merchantId: string;
  name: string;
  fee: number;
  fixedLoadAmounts: number[];
  states: string[];
  logoUrl: string;
}

export interface IFundingStoreAddress {
  line1: string;
  city: string;
  state: string;
  zip: string;
  latitude: number;
  longitude: number;
}

/** Physical store from GET /widget/operations/funding-locations/stores. */
export interface IFundingStore {
  storeId: string;
  merchantId: string;
  name: string;
  logoUrl: string;
  address: IFundingStoreAddress;
  distanceMiles?: number;
}

export interface IFundingStoresQuery {
  lat?: number;
  lng?: number;
  merchantId?: string;
}

/**
 * List cash-deposit funding locations (merchant chains) for the corridor.
 * GET /widget/operations/funding-locations
 */
export const getFundingLocations = async (
  state?: string
): Promise<IFundingMerchant[]> => {
  const { data } = await widgetApi.get("/widget/operations/funding-locations", {
    params: state ? { state } : undefined,
  });
  return data.data;
};

/**
 * Nearby physical stores for the locator map.
 * GET /widget/operations/funding-locations/stores
 */
export const getFundingStores = async (
  query: IFundingStoresQuery = {},
): Promise<IFundingStore[]> => {
  const params: Record<string, string> = {};
  if (query.lat != null && query.lng != null) {
    params.lat = String(query.lat);
    params.lng = String(query.lng);
  }
  if (query.merchantId) {
    params.merchantId = query.merchantId;
  }
  const { data } = await widgetApi.get(
    "/widget/operations/funding-locations/stores",
    { params },
  );
  return data.data;
};
